import { ref } from 'vue';
import { useRouter, type Router } from 'vue-router';
import { extractApiErrorMessage, extractApiFieldErrors, useCustomToast } from '@/shared/lib';
import { buildFieldErrorMessage } from '../lib/buildFieldErrorMessage';
import { logNavigationFailure } from '../lib/logNavigationFailure';

const TRAINING_LIST_ROUTE = '/treinamentos/lista-treinamentos';

export interface SubmitTrainingOptions {
  mode?: 'create' | 'edit';
  onFieldErrors?: (fieldErrors: Record<string, string[]>) => void;
  /**
   * Fires synchronously right before the success redirect, still inside the
   * same tick that calls router.push. Lets a caller (the edit dispatcher)
   * disarm its own leave-guard before that push, with no race against when
   * the router actually resolves the navigation.
   */
  onSuccess?: () => void;
}

/**
 * The submit lifecycle every training creation form shares: guard against
 * double submits, call the type-specific endpoint, then toast + redirect to
 * the created training's view on success, or toast the backend's message
 * (or a generic fallback) on failure.
 *
 * In `edit` mode the error toast is a single fixed string with no technical
 * detail (field errors included) and the success redirect lands on the list,
 * not the view — matching the precedent in useEditTrainingContent.ts.
 *
 * `routerOverride`: the edit dispatcher (useEditTraining) instantiates the
 * type-specific form composable — and therefore this one, transitively —
 * only after an `await` (it needs the fetched training's type first). By
 * then Vue's active component instance is gone, so useRouter()'s inject()
 * silently returns undefined. Callers on that path must pass down a router
 * captured synchronously earlier; callers on a synchronous path (creation)
 * can omit it and rely on useRouter() as before.
 */
export function useTrainingFormSubmit(routerOverride?: Router) {
  const toast = useCustomToast();
  const router = routerOverride ?? useRouter();
  const saving = ref(false);

  async function submit<T>(
    canSave: boolean,
    buildPayload: () => T | null,
    createTraining: (payload: T) => Promise<{ data: { id: number } }>,
    options: SubmitTrainingOptions = {}
  ): Promise<void> {
    if (!canSave || saving.value) return;

    const payload = buildPayload();
    if (payload === null) return;

    const isEdit = options.mode === 'edit';

    saving.value = true;
    // The outer finally is the only place that clears `saving`: whatever happens below —
    // the network call failing, or even an unexpected throw from the toast/onSuccess/push
    // afterwards — the Salvar button must never get stuck disabled forever.
    try {
      let response: { data: { id: number } };
      try {
        response = await createTraining(payload);
      } catch (error: unknown) {
        if (isEdit) {
          toast.showErrorToast({ title: 'Erro ao editar treinamento. Tente novamente', type: 'error' });
          return;
        }

        const fieldErrors = extractApiFieldErrors(error);
        if (Object.keys(fieldErrors).length > 0) {
          options.onFieldErrors?.(fieldErrors);
          toast.showErrorToast({
            title: 'Erro ao salvar treinamento',
            message: buildFieldErrorMessage(fieldErrors),
            type: 'error',
          });
          return;
        }

        toast.showErrorToast({
          title: 'Erro ao criar treinamento',
          message: extractApiErrorMessage(error) ?? 'Tente novamente',
          type: 'error',
        });
        return;
      }

      // Deliberately outside the inner try above: once createTraining has resolved, the
      // save already succeeded, so nothing past this point should ever be able to surface
      // as an "erro ao editar/criar" toast on top of the success one already shown.
      toast.showSuccessToast({
        title: isEdit ? 'Treinamento editado com sucesso' : 'Treinamento criado com sucesso',
        type: 'success',
      });
      options.onSuccess?.();
      const navigationResult = await router.push(
        isEdit ? TRAINING_LIST_ROUTE : `${TRAINING_LIST_ROUTE}/${response.data.id}`
      );
      logNavigationFailure('useTrainingFormSubmit', navigationResult);
    } finally {
      // Cleared only after the redirect is kicked off, not before, so the "Salvar" button
      // never flashes back to enabled while the leave-guard/route change is still resolving.
      saving.value = false;
    }
  }

  return { saving, submit };
}
